import React, { useState } from "react";
import { X, Upload, FileText, Settings } from "lucide-react";

const ReplaceTorrentModal = ({ isOpen, onClose, torrent, onSuccess }) => {
  const [formData, setFormData] = useState({
    tracker_replacement_enabled: torrent?.tracker_replacement_enabled ?? true,
  });
  const [torrentFile, setTorrentFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".torrent")) {
        setErrors({ torrentFile: "Please select a valid .torrent file" });
        setTorrentFile(null);
        return;
      }
      setTorrentFile(file);
      setErrors((prev) => ({ ...prev, torrentFile: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!torrentFile) {
      newErrors.torrentFile = "Torrent file is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const fileReader = new FileReader();
      fileReader.onload = async () => {
        const arrayBuffer = fileReader.result;
        const base64Data = btoa(
          new Uint8Array(arrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ""
          )
        );

        const updateData = {
          tracker_replacement_enabled: formData.tracker_replacement_enabled,
          torrent_file_data: base64Data,
          torrent_filename: torrentFile.name,
        };

        try {
          const response = await fetch(`/api/admin/torrents/${torrent.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `tma ${window.Telegram.WebApp.initData}`,
            },
            body: JSON.stringify(updateData),
          });

          const result = await response.json();

          if (response.ok) {
            onSuccess && onSuccess(result.torrent);
            handleClose();
          } else {
            setErrors({
              submit: result.error || "Failed to replace torrent file",
            });
          }
        } catch (error) {
          console.error("Error replacing torrent:", error);
          setErrors({ submit: "Network error occurred" });
        } finally {
          setIsSubmitting(false);
        }
      };

      fileReader.onerror = () => {
        setErrors({ submit: "Failed to read the file" });
        setIsSubmitting(false);
      };

      fileReader.readAsArrayBuffer(torrentFile);
    } catch (error) {
      console.error("Error reading file:", error);
      setErrors({ submit: "Failed to process the file" });
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      tracker_replacement_enabled: torrent?.tracker_replacement_enabled ?? true,
    });
    setTorrentFile(null);
    setIsSubmitting(false);
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 mx-4 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText size={20} />
            Replace Torrent File
          </h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current torrent info */}
          <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Current torrent:
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {torrent?.title}
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              New Torrent File *
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".torrent"
                onChange={handleFileChange}
                className="hidden"
                id="torrent-file-input"
              />
              <label
                htmlFor="torrent-file-input"
                className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                  torrentFile
                    ? "border-green-300 bg-green-50 dark:border-green-600 dark:bg-green-900/20"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                }`}
              >
                <Upload size={20} className="text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {torrentFile
                    ? `Selected: ${torrentFile.name}`
                    : "Click to select .torrent file"}
                </span>
              </label>
            </div>
            {errors.torrentFile && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {errors.torrentFile}
              </p>
            )}
          </div>

          {/* Tracker Replacement Option */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <Settings size={16} className="inline mr-1" />
              Tracker Settings
            </label>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <input
                type="checkbox"
                id="tracker_replacement"
                checked={formData.tracker_replacement_enabled}
                onChange={(e) =>
                  handleInputChange(
                    "tracker_replacement_enabled",
                    e.target.checked
                  )
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
              />
              <label
                htmlFor="tracker_replacement"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                Enable tracker replacement
              </label>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 px-3">
              When enabled, the original tracker will be replaced with working
              alternatives.
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                {errors.submit}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !torrentFile}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Replacing...
                </>
              ) : (
                "Replace File"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReplaceTorrentModal;
